import { motion } from "framer-motion";
import { fr } from "date-fns/locale";
import { AlertTriangle, Calendar as CalendarIcon, CheckCircle2, Clock, Globe2, Loader2, MapPin, MessageCircle, Save, User2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { COMPANY_TZ_LABEL, browserTz, isRemoteTz, parisParts, parisTimeToBrowserWall, parisWallToUtc } from "../../lib/tz";
import { Calendar } from "../ui/calendar";

/**
 * Schedule a field immersion for a candidate.
 *
 * Props:
 *  - candidate (object) — used for the header (first_name, last_name, city)
 *  - onCancel ()        — close without saving
 *  - onSuccess (data)   — called when the immersion is scheduled (receives updated candidate)
 */
export default function ScheduleImmersionModal({ candidate, onCancel, onSuccess }) {
    const [referents, setReferents] = useState([]);
    const [loadingTeam, setLoadingTeam] = useState(true);
    // If the candidate already has an immersion programmed (reschedule case),
    // pre-fill the form with those values so the user only edits what's wrong.
    // ALL datetimes are interpreted in Europe/Paris (Shariti company TZ),
    // regardless of where the pre-recruteur is physically located.
    const existingParts = candidate.immersion_scheduled_at
        ? parisParts(candidate.immersion_scheduled_at)
        : null;
    const [date, setDate] = useState(() => {
        if (existingParts) {
            // Build a local Date whose calendar day matches the Paris day —
            // the calendar picker only uses year/month/day (time not read).
            return new Date(existingParts.year, existingParts.month - 1, existingParts.day);
        }
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(10, 0, 0, 0);
        return d;
    });
    const [time, setTime] = useState(() => {
        if (existingParts) {
            const hh = String(existingParts.hour).padStart(2, "0");
            const mm = String(existingParts.minute).padStart(2, "0");
            return `${hh}:${mm}`;
        }
        return "10:00";
    });
    const [location, setLocation] = useState(candidate.immersion_location || candidate.city || "");
    const [referentId, setReferentId] = useState(candidate.immersion_referent_id || candidate.referent_id || "");
    const [notes, setNotes] = useState(candidate.immersion_notes || "");
    const [submitting, setSubmitting] = useState(false);
    // When true (default), after saving we open the SendMessageModal with the
    // recap template pre-selected so the candidate receives the updated info.
    // The user explicitly asked for this : "il faudra absolument que j'aie
    // automatiquement la possibilité d'obtenir le WhatsApp actualisé, envoyé
    // directement automatiquement au candidat" — we don't auto-send (Twilio
    // not branché yet) but we open the modal one click away.
    const [notifyAfter, setNotifyAfter] = useState(true);
    const isReschedule = !!candidate.immersion_scheduled_at;

    // Find the picked referent in the loaded list so we can warn IMMEDIATELY
    // if they don't have a phone number — otherwise the WhatsApp template
    // will render "joignez * au *" as in the production incident.
    const pickedReferent = useMemo(
        () => referents.find((r) => r.id === referentId) || null,
        [referents, referentId]
    );
    const referentMissingPhone = pickedReferent && !((pickedReferent.phone || "").trim());

    // Aggregated validation state — used to disable the submit button AND to
    // render the "Données manquantes" banner.
    const validationErrors = useMemo(() => {
        const errs = [];
        if (!date) errs.push({ key: "date", label: "Sélectionnez une date" });
        if (!time || !/^\d{2}:\d{2}$/.test(time)) errs.push({ key: "time", label: "Renseignez une heure valide (HH:MM)" });
        if (!location.trim()) errs.push({ key: "location", label: "Indiquez l'adresse précise du lieu d'immersion" });
        if (!referentId) errs.push({ key: "referent", label: "Assignez un référent terrain (obligatoire)" });
        if (referentMissingPhone) {
            errs.push({
                key: "referent_phone",
                label: `Le référent ${pickedReferent.name} n'a pas de téléphone renseigné dans son profil`,
            });
        }
        return errs;
    }, [date, time, location, referentId, referentMissingPhone, pickedReferent]);
    const canSubmit = validationErrors.length === 0 && !submitting;

    useEffect(() => {
        async function fetchTeam() {
            try {
                const { data } = await api.get("/users/team");
                setReferents(data.referents || []);
                if (data.referents?.length === 1) {
                    setReferentId(data.referents[0].id);
                }
            } catch {
                toast.error("Impossible de charger les référents");
            } finally {
                setLoadingTeam(false);
            }
        }
        fetchTeam();
    }, []);

    // Esc closes modal
    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape") onCancel();
        }
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [onCancel]);

    async function submit() {
        // Defensive — UI prevents submission but we double-check.
        if (validationErrors.length > 0) {
            toast.error(validationErrors[0].label);
            return;
        }
        // Interpret the picked date + "HH:MM" as Europe/Paris wall clock and
        // convert to a real UTC instant. This way Sarah in Cotonou typing
        // "16:19" produces the SAME UTC as the admin in Paris typing "16:19".
        const [hh, mm] = time.split(":").map((n) => parseInt(n, 10));
        const combined = parisWallToUtc(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
            hh,
            mm,
        );
        if (combined < new Date()) {
            toast.error("La date d'immersion doit être dans le futur");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/schedule-immersion`, {
                scheduled_at: combined.toISOString(),
                location: location.trim(),
                referent_id: referentId,
                notes: notes.trim() || null,
            });
            toast.success(isReschedule ? "Immersion mise à jour" : "Immersion programmée");
            // Bubble both the updated candidate AND the "notify-after" intent
            // so the caller (ImmersionCalendar / CandidateProfile) opens the
            // SendMessageModal with the recap template selected. We do NOT
            // open the modal from inside ScheduleImmersionModal to keep the
            // component reusable.
            onSuccess?.(data, { notifyAfter });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid="schedule-immersion-modal"
            onClick={onCancel}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-3xl w-full max-h-[90vh] flex flex-col my-8"
            >
                <div className="flex items-start justify-between gap-4 mb-5 shrink-0">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <CalendarIcon className="h-3 w-3" /> Planifier l'immersion
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">
                            {candidate.first_name} {candidate.last_name}
                        </h2>
                        <p className="mt-1 text-sm font-medium text-slate-500">
                            Sélectionnez une date, l'heure, le lieu et le référent terrain en charge.
                        </p>
                    </div>
                    <button type="button" onClick={onCancel} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 grid md:grid-cols-2 gap-6 -mx-2 px-2">
                    {/* Left: Calendar */}
                    <div className="space-y-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            Date d'immersion
                        </div>
                        <div className="rounded-2xl border border-shariti-gray bg-shariti-cream/40 p-2 flex justify-center">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={setDate}
                                locale={fr}
                                weekStartsOn={1}
                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                data-testid="immersion-calendar"
                            />
                        </div>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
                                <Clock className="h-3 w-3" /> Heure <span className="text-shariti-orange">· {COMPANY_TZ_LABEL}</span>
                            </span>
                            <input
                                type="time"
                                value={time}
                                onChange={(e) => setTime(e.target.value)}
                                data-testid="immersion-time"
                                className="input-shariti"
                            />
                            {isRemoteTz() && time && /^\d{2}:\d{2}$/.test(time) && (
                                <div
                                    className="mt-1.5 rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 text-[11px] font-semibold text-sky-800 inline-flex items-start gap-1.5"
                                    data-testid="tz-hint"
                                >
                                    <Globe2 className="h-3 w-3 mt-0.5 shrink-0" />
                                    <span>
                                        <strong>{time}</strong> à Paris ≈ <strong>
                                            {parisTimeToBrowserWall(
                                                parseInt(time.split(":")[0], 10),
                                                parseInt(time.split(":")[1], 10),
                                            )}
                                        </strong> chez vous ({browserTz()})
                                    </span>
                                </div>
                            )}
                        </label>
                    </div>

                    {/* Right: Form */}
                    <div className="space-y-4">
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
                                <MapPin className="h-3 w-3" /> Lieu d'immersion
                            </span>
                            <input
                                type="text"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                placeholder="ex. Paris 11e — Bastille / Tour Eiffel"
                                data-testid="immersion-location"
                                className="input-shariti"
                            />
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1.5">
                                <User2 className="h-3 w-3" /> Référent terrain en charge
                            </span>
                            {loadingTeam ? (
                                <div className="input-shariti flex items-center gap-2 text-slate-400">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
                                </div>
                            ) : referents.length === 0 ? (
                                <div className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                                    Aucun référent terrain dans l'équipe. Créez-en un depuis <strong>Équipe</strong>.
                                </div>
                            ) : (
                                <select
                                    value={referentId}
                                    onChange={(e) => setReferentId(e.target.value)}
                                    data-testid="immersion-referent"
                                    className={`input-shariti ${
                                        !referentId
                                            ? "border-2 border-shariti-red/40 bg-red-50/40"
                                            : referentMissingPhone
                                            ? "border-2 border-amber-400 bg-amber-50/40"
                                            : ""
                                    }`}
                                >
                                    <option value="">— Sélectionnez un référent (obligatoire) —</option>
                                    {referents.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.name}{!((r.phone || "").trim()) ? "  (sans téléphone)" : ""}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {referentId && referentMissingPhone && (
                                <div
                                    className="mt-1.5 rounded-xl bg-amber-50 border border-amber-300 px-3 py-2 text-[11px] font-semibold text-amber-800"
                                    data-testid="referent-phone-warning"
                                >
                                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                                    {pickedReferent?.name} n'a pas de téléphone dans son profil. Le WhatsApp candidat afficherait <em>« joignez * au * »</em>. Ajoutez son numéro depuis l'<a href="/utilisateurs" className="underline">équipe</a>.
                                </div>
                            )}
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                                Notes logistiques (optionnel)
                            </span>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Transport, contact sur place, créneau précis…"
                                data-testid="immersion-notes"
                                rows={3}
                                className="input-shariti resize-none"
                            />
                        </label>

                        {/* Live validation banner — shows EXACTLY what's missing
                            and disables the submit until everything is set. */}
                        {validationErrors.length > 0 && (
                            <div
                                className="rounded-2xl border-2 border-shariti-red/40 bg-red-50 p-3"
                                data-testid="schedule-validation-banner"
                            >
                                <div className="flex items-start gap-2 mb-2">
                                    <AlertTriangle className="h-4 w-4 text-shariti-red shrink-0 mt-0.5" />
                                    <div className="text-xs font-extrabold text-shariti-red">
                                        {validationErrors.length} information{validationErrors.length > 1 ? "s" : ""} à compléter avant {isReschedule ? "de mettre à jour" : "de programmer"} l'immersion
                                    </div>
                                </div>
                                <ul className="space-y-1 pl-6">
                                    {validationErrors.map((e) => (
                                        <li
                                            key={e.key}
                                            data-testid={`schedule-err-${e.key}`}
                                            className="text-[12px] font-semibold text-shariti-red flex items-start gap-1.5"
                                        >
                                            <span className="h-1.5 w-1.5 mt-1.5 rounded-full bg-shariti-red shrink-0" />
                                            {e.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {date && (
                            <div className="rounded-2xl bg-shariti-orange-soft/50 border border-shariti-orange/20 p-3">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-shariti-orange mb-1">
                                    Récapitulatif
                                </div>
                                <div className="text-sm font-extrabold text-shariti-black">
                                    {date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {time}
                                </div>
                                {location && (
                                    <div className="text-xs font-medium text-slate-600 mt-1 flex items-center gap-1.5">
                                        <MapPin className="h-3 w-3" /> {location}
                                    </div>
                                )}
                                {pickedReferent && (
                                    <div className="text-xs font-medium text-slate-600 mt-1 flex items-center gap-1.5">
                                        <User2 className="h-3 w-3" /> {pickedReferent.name}
                                        {!referentMissingPhone && pickedReferent.phone && (
                                            <span className="text-emerald-600">· {pickedReferent.phone}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Auto-notify candidate — opens SendMessageModal on save */}
                        <label
                            className="flex items-start gap-2.5 cursor-pointer rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 p-3 transition"
                            data-testid="notify-after-save"
                        >
                            <input
                                type="checkbox"
                                checked={notifyAfter}
                                onChange={(e) => setNotifyAfter(e.target.checked)}
                                data-testid="notify-after-save-checkbox"
                                className="mt-0.5 h-4 w-4 rounded accent-emerald-500"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-extrabold text-emerald-800 inline-flex items-center gap-1.5">
                                    <MessageCircle className="h-3.5 w-3.5" />
                                    Notifier {candidate.first_name} sur WhatsApp après {isReschedule ? "mise à jour" : "programmation"}
                                </div>
                                <div className="text-[11px] font-medium text-emerald-700/85 mt-0.5">
                                    {isReschedule
                                        ? "Le candidat reçoit le récap avec la nouvelle date, le nouveau lieu et le nouveau référent. Indispensable après une modification."
                                        : "Ouvre le récap WhatsApp prêt à envoyer dès que vous cliquez Programmer."}
                                </div>
                            </div>
                            {notifyAfter && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />}
                        </label>
                    </div>
                </div>

                <div className="mt-5 flex items-center justify-end gap-3 shrink-0 border-t border-shariti-gray pt-4">
                    <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition">
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="immersion-submit"
                        title={!canSubmit && validationErrors.length > 0 ? validationErrors[0].label : undefined}
                        className="btn-shariti disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {isReschedule ? "Mettre à jour l'immersion" : "Programmer l'immersion"}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}
